import { Injectable, inject } from '@angular/core';
import {
  CenterOnExtent,
  CenterOnLocation,
  LoadSearchResultsSuccess,
  MapContextActionTypes as ArableLibMapContextActionTypes,
  SearchLocation,
} from '@next/arable-lib/geo';
import { GeoSearchLocationService } from '@next/arable-lib/geo';
import { GeoService, GeoServiceInjector } from '@next/arable-lib/geo';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { extend } from 'ol/extent';
import Feature from 'ol/Feature';
import { GeoJSON } from 'ol/format';
import { Geometry } from 'ol/geom';
import { EMPTY, Observable, of } from 'rxjs';
import { switchMap, withLatestFrom, map, tap, filter, mergeMap, delay } from 'rxjs/operators';

import {
  AskImportRegulatoryManureFields,
  AskSynchronizeCropFields,
  AskSynchronizeCropFieldsRvO,
  SelectCustomerForImport,
} from 'src/app/registration/store/actions/crop-field.actions';
import { SiteState } from 'src/app/shared/models';

import { InteractionType } from '../models';
import {
  FeatureActionTypes,
  InteractionFeatureDeselected,
  InteractionFeatureSelected,
  InteractionFeatureToggle,
  DeselectCropFields,
} from './actions/feature.actions';
import {
  InteractionReady,
  MapContextActionTypes,
  SetCropFieldsEnabled,
  SetSoilAnalysesEnabled,
  SetRvoEnabled,
  SetInteractionType,
} from './actions/map-context.actions';
import {
  getFeatures,
  getLastYearsFeatures,
  getSelectedFeatures,
  getSelectedFeaturesIdsAsNumber,
} from './selectors/feature.selectors';

@Injectable()
export class Effects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly geoSearchLocationService = inject(GeoSearchLocationService);
  private readonly geoService = inject<GeoService>(GeoServiceInjector);

  interactionReady$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FeatureActionTypes.InteractionFeatureDrawn,
        FeatureActionTypes.InteractionFeatureModified,
      ),
      switchMap(() => of(new InteractionReady())),
    ),
  );

  geoServiceInitialized$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ArableLibMapContextActionTypes.GeoServiceInitialized),
      map(() => new SetInteractionType(InteractionType.selectCropField)),
    ),
  );

  updateSize$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ArableLibMapContextActionTypes.UpdateSize),
        delay(1),
        tap(() => this.geoService.map.updateSize()),
      ),
    { dispatch: false },
  );

  zoomIn$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ArableLibMapContextActionTypes.ZoomIn),
        tap(() => this.geoService.zoomIn()),
      ),
    { dispatch: false },
  );

  zoomOut$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ArableLibMapContextActionTypes.ZoomOut),
        tap(() => this.geoService.zoomOut()),
      ),
    { dispatch: false },
  );

  interactionFeatureToggle$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureActionTypes.InteractionFeatureToggle),
      withLatestFrom(this.store.pipe(select(getSelectedFeaturesIdsAsNumber))),
      map(([action, selectedIds]: [InteractionFeatureToggle, number[]]) =>
        selectedIds.indexOf(action.id) === -1
          ? new InteractionFeatureSelected(action.id)
          : new InteractionFeatureDeselected(action.id),
      ),
    ),
  );

  selectCropField$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MapContextActionTypes.SetInteractionType),
      filter(({ interactionType }) => interactionType === InteractionType.selectCropField),
      mergeMap(() => [new SetCropFieldsEnabled(true), new SetSoilAnalysesEnabled(false)]),
    ),
  );

  selectSoilanalyses$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MapContextActionTypes.SetInteractionType),
      filter(({ interactionType }) => interactionType === InteractionType.selectSoilAnalysis),
      map(() => new SetSoilAnalysesEnabled(true)),
    ),
  );

  linkSoilanalyses$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MapContextActionTypes.SetInteractionType),
      filter(
        ({ interactionType }) => interactionType === InteractionType.linkSoilAnalysisToCropField,
      ),
      mergeMap(() => [new SetCropFieldsEnabled(true), new SetSoilAnalysesEnabled(true)]),
    ),
  );

  interactionTypeModify$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MapContextActionTypes.SetInteractionType),
      filter(({ interactionType }) => interactionType === InteractionType.modifyFeature),
      map(() => new DeselectCropFields()),
    ),
  );

  interactionTypeRvo$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MapContextActionTypes.SetInteractionType),
      filter(({ interactionType }) => interactionType === InteractionType.cloneRvo),
      map(() => new SetRvoEnabled(true)),
    ),
  );

  interactionSyncCropFieldsWithBms$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MapContextActionTypes.SetInteractionType),
      filter(
        ({ interactionType }) => interactionType === InteractionType.synchronizeCropFieldsWithBMS,
      ),
      map(() => new AskSynchronizeCropFields()),
    ),
  );

  interactionSyncCropFieldsWithRvo$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MapContextActionTypes.SetInteractionType),
      filter(
        ({ interactionType }) => interactionType === InteractionType.synchronizeCropFieldsWithRvO,
      ),
      map(() => new AskSynchronizeCropFieldsRvO()),
    ),
  );

  interactionImportRegulatoryManureFields: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MapContextActionTypes.SetInteractionType),
      filter(
        ({ interactionType }) => interactionType === InteractionType.importRegulatoryManureFields,
      ),
      map(() => new AskImportRegulatoryManureFields()),
    ),
  );

  interactionImportCropFieldsFromCustomer$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(MapContextActionTypes.SetInteractionType),
      filter(
        ({ interactionType }) => interactionType === InteractionType.importCropFieldsFromCustomer,
      ),
      map(() => new SelectCustomerForImport()),
    ),
  );

  centerOnLocation$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ArableLibMapContextActionTypes.CenterOnLocation),
        tap((action: CenterOnLocation) => this.geoService.centerOnLocation(action.coordinates)),
      ),
    { dispatch: false },
  );

  searchLocation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ArableLibMapContextActionTypes.SearchLocation),
      mergeMap((action: SearchLocation) =>
        this.geoSearchLocationService
          .searchLocation(action.query)
          .pipe(map((searchResults) => new LoadSearchResultsSuccess(searchResults))),
      ),
    ),
  );

  centerOnSelection$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ArableLibMapContextActionTypes.CenterOnSelection),
      withLatestFrom(
        this.store.pipe(select(getSelectedFeatures)),
        this.store.pipe(select(getFeatures)),
        this.store.pipe(select(getLastYearsFeatures)),
      ),
      switchMap(([_, selectedFeatures, features, lastYearFeatures]) => {
        const reader = new GeoJSON();
        let featuresForExtent: Feature<Geometry>[] = [];
        if (selectedFeatures && selectedFeatures.length > 0) {
          featuresForExtent = selectedFeatures.map((feature) => reader.readFeature(feature));
        } else if (features && features.length > 0) {
          featuresForExtent = features.map((feature) => reader.readFeature(feature));
        } else if (lastYearFeatures && lastYearFeatures.length > 0) {
          featuresForExtent = lastYearFeatures.map((feature) => reader.readFeature(feature));
        }

        if (featuresForExtent.length === 0) {
          return EMPTY;
        }

        const extent = featuresForExtent[0].getGeometry().getExtent();
        for (let index = 1; index < featuresForExtent.length; index++) {
          extend(extent, featuresForExtent[index].getGeometry().getExtent());
        }
        return of(new CenterOnExtent(extent));
      }),
    ),
  );

  centerOnExtent$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ArableLibMapContextActionTypes.CenterOnExtent),
        tap((action: CenterOnExtent) => {
          this.geoService.centerOnExtent(action.extent);
        }),
      ),
    { dispatch: false },
  );
}
